import React, { useState, useEffect, useMemo, useCallback } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  getCountryScreeners,
  getSchoolScreeners,
  getAssessmentScreener,
  getMyTrialScreenerAccess,
} from "../services/APIClient";
import {
  hasScreenerGradeAccess,
  isScreenerAvailable,
  isPurchaseExpired,
} from "../utils/screenerAccess";
import "./AssessmentTable.css";

const ASSESSMENT_ORDER = {
  baseline: 1,
  midpoint: 2,
  "end of year": 3,
};

const GRADE_ABBREVIATIONS = {
  "Standard 1": "S1",
  "Standard 2": "S2",
  Reception: "Recep",
  "Year 1": "Y1",
  "Primary 1": "P1",
  "Primary 2": "P2",
  "Pre-K": "Pre-K",
  "Kindergarten (K)": "K",
  "KG1 (Kindergarten 1)": "KG1",
  "KG2 (Kindergarten 2)": "KG2",
  "Junior Infants": "Jnr",
  "Senior Infants": "Snr",
};

const normalizeId = (assessment) =>
  String(assessment?.assessmentid ?? assessment?.id ?? "");

function AssessmentTable({
  selectAssessmentId,
  setSelectAssessmentId,
  setSelectAssessmentName,
  classDetails,
  gradeLevelOptions,
  schoolid,
  countryId,
  onAssessmentsLoaded,
  isJIOnlyTrial,
  isPilot,
  latestPurchase,
  purchaseAccessError,
}) {
  const [assessments, setAssessments] = useState([]);
  const [loadingAssessments, setLoadingAssessments] = useState(true);
  const [assessmentError, setAssessmentError] = useState(null);

  const [screenerSettings, setScreenerSettings] = useState({});
  const [loadingScreeners, setLoadingScreeners] = useState(true);
  const [screenerError, setScreenerError] = useState(null);
  const [trialAccess, setTrialAccess] = useState(null);
  const [loadingTrialAccess, setLoadingTrialAccess] = useState(true);
  const [trialAccessError, setTrialAccessError] = useState(null);

  const gradeLevel1 = classDetails?.grade_level1;
  const gradeLevel2 = classDetails?.grade_level2;
  const gradeLevel3 = classDetails?.grade_level3;
  const gradeLevel4 = classDetails?.grade_level4;
  const grade1Abbrev = GRADE_ABBREVIATIONS[gradeLevelOptions?.[0]?.grade1];
  const grade2Abbrev = GRADE_ABBREVIATIONS[gradeLevelOptions?.[0]?.grade2];
  const grade3Abbrev = GRADE_ABBREVIATIONS[gradeLevelOptions?.[0]?.grade3];
  const grade4Abbrev = GRADE_ABBREVIATIONS[gradeLevelOptions?.[0]?.grade4];

  // Helper functions
  const getAssessmentOrder = (name) => {
    const normalizedName = (name || "").toLowerCase();
    if (/\bbaseline\b/.test(normalizedName)) return ASSESSMENT_ORDER.baseline;
    if (/\bmid\s*point\b/.test(normalizedName)) return ASSESSMENT_ORDER.midpoint;
    if (/\bend[-\s]*of[-\s]*year\b/.test(normalizedName)) return ASSESSMENT_ORDER["end of year"];
    return 4;
  };

  const sortAssessments = (a, b) => {
    const orderDiff = getAssessmentOrder(a.name) - getAssessmentOrder(b.name);
    return orderDiff !== 0 ? orderDiff : String(a.name || "").localeCompare(String(b.name || ""));
  };

  const replaceGradePrefix = (name) =>
    String(name || "")
      .replace(/\bJunior\b/i, grade1Abbrev || "Grade 1")
      .replace(/\bSenior\b/i, grade2Abbrev || "Grade 2");

  const getAssessmentImageName = (name) => {
    const normalizedName = String(name || "").toLowerCase();
    if (/\bbaseline\b/.test(normalizedName)) return "autumn";
    if (/\bmid\s*point\b/.test(normalizedName)) return "spring";
    if (/\bend[-\s]*of[-\s]*year\b/.test(normalizedName)) return "summer";
    return normalizedName.split(/\s+/)[0] || "assessment";
  };

  const coerceCountryId = (id) => {
    return typeof id === "string" && /^\d+$/.test(id) ? Number(id) : id;
  };

  const deduplicateAssessments = (assessmentList) => {
    const dedupMap = new Map();
    for (const assessment of assessmentList || []) {
      const key = normalizeId(assessment);
      if (key && !dedupMap.has(key)) {
        dedupMap.set(key, assessment);
      }
    }
    return Array.from(dedupMap.values());
  };

  const fetchAssessmentsForGrade = async (countryIdCoerced, gradeLevel) => {
    const params = {
      countryid: countryIdCoerced,
      grade_level: gradeLevel,
      active: true,
      standard: true,
    };
    
    try {
      const result = await getAssessmentScreener(params);
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error(`Error fetching grade ${gradeLevel} assessments:`, error);
      return [];
    }
  };

  const fetchAssessmentsForClass = async (countryIdCoerced) => {
    let allAssessments = [];

    // Fetch for each grade level
    if (gradeLevel1) {
      const grade1Assessments = await fetchAssessmentsForGrade(countryIdCoerced, 1);
      allAssessments = [...allAssessments, ...grade1Assessments];
    }

    if (gradeLevel2) {
      const grade2Assessments = await fetchAssessmentsForGrade(countryIdCoerced, 2);
      allAssessments = [...allAssessments, ...grade2Assessments];
    }

    if (gradeLevel3) {
      const grade3Assessments = await fetchAssessmentsForGrade(countryIdCoerced, 3);
      allAssessments = [...allAssessments, ...grade3Assessments];
    }

    if (gradeLevel4) {
      const grade4Assessments = await fetchAssessmentsForGrade(countryIdCoerced, 4);
      allAssessments = [...allAssessments, ...grade4Assessments];
    }

    // Fallback if no grade levels specified
    if (!gradeLevel1 && !gradeLevel2 && !gradeLevel3 && !gradeLevel4) {
      try {
        const fallbackAssessments = await getAssessmentScreener({
          countryid: countryIdCoerced,
          active: true,
          standard: true,
        });
        if (Array.isArray(fallbackAssessments)) {
          allAssessments = [...allAssessments, ...fallbackAssessments];
        }
      } catch (error) {
        console.error("Error fetching fallback assessments:", error);
      }
    }

    // Deduplicate
    allAssessments = deduplicateAssessments(allAssessments);

    // Never substitute assessments from another territory.
    const matchingAssessments = allAssessments.filter(a => a.countryid === countryIdCoerced);
    return matchingAssessments;
  };

  // Fetch assessments
  useEffect(() => {
    let isMounted = true;

    const fetchAssessments = async () => {
      if (!countryId) {
        console.error("No countryId provided to AssessmentTable");
        setLoadingAssessments(false);
        return;
      }

      try {
        setLoadingAssessments(true);
        setAssessmentError(null);

        const countryIdCoerced = coerceCountryId(countryId);
        const fetchedAssessments = await fetchAssessmentsForClass(countryIdCoerced);

        if (!isMounted) return;
        setAssessments(fetchedAssessments);
      } catch (error) {
        console.error("Error fetching assessments:", error);
        if (!isMounted) return;
        setAssessmentError(error);
        setAssessments([]);
      } finally {
        if (isMounted) setLoadingAssessments(false);
      }
    };

    fetchAssessments();
    return () => {
      isMounted = false;
    };
  }, [countryId, gradeLevel1, gradeLevel2, gradeLevel3, gradeLevel4]);

  useEffect(() => {
    let isMounted = true;
    setLoadingTrialAccess(true);
    setTrialAccessError(null);
    getMyTrialScreenerAccess()
      .then((result) => {
        if (isMounted) setTrialAccess(result);
      })
      .catch((error) => {
        if (!isMounted) return;
        setTrialAccess(null);
        setTrialAccessError(error);
      })
      .finally(() => {
        if (isMounted) setLoadingTrialAccess(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch screener settings
  useEffect(() => {
    let isMounted = true;

    const fetchScreenerSettings = async () => {
      if (!schoolid) {
        console.error("No schoolId provided to AssessmentTable");
        setLoadingScreeners(false);
        return;
      }

      try {
        setLoadingScreeners(true);
        setScreenerError(null);

        // Try school screeners first
        let screeners = await getSchoolScreeners(schoolid);

        // Fallback to country screeners if no school screeners
        if (!screeners || screeners.length === 0) {
          screeners = await getCountryScreeners(schoolid);
        }

        // Create screener map
        const screenerMap = {};
        const now = new Date();
        if (Array.isArray(screeners)) {
          screeners.forEach((screener) => {
            const key = String(screener?.assessmentid ?? screener?.id ?? "");
            if (key) {
              const isOpen =
                !screener?.dateopened || new Date(screener.dateopened) <= now;
              const isNotClosed =
                !screener?.dateclosed || new Date(screener.dateclosed) >= now;
              const legacyAvailability =
                !!screener?.active && isOpen && isNotClosed;
              screenerMap[key] =
                typeof screener?.available === "boolean"
                  ? screener.available
                  : legacyAvailability;
            }
          });
        }

        if (!isMounted) return;
        setScreenerSettings(screenerMap);
      } catch (error) {
        console.error("Error fetching screener settings:", error);
        if (!isMounted) return;
        setScreenerError(error);
        setScreenerSettings({});
      } finally {
        if (isMounted) setLoadingScreeners(false);
      }
    };

    fetchScreenerSettings();
    return () => {
      isMounted = false;
    };
  }, [schoolid, countryId]);

  // Process assessments
  const processedAssessments = useMemo(() => {
    if (!assessments || assessments.length === 0) {
      return [];
    }

    const processed = assessments.map((assessment) => ({
      ...assessment,
      name: replaceGradePrefix(assessment.name),
      imageName: getAssessmentImageName(assessment.name),
      __idKey: normalizeId(assessment),
    }));

    // Deduplicate
    const deduplicated = deduplicateAssessments(processed);

    return deduplicated.sort(sortAssessments);
  }, [
    assessments,
    grade1Abbrev,
    grade2Abbrev,
    grade3Abbrev,
    grade4Abbrev,
  ]);

  // Notify parent component when assessments are loaded
  useEffect(() => {
    if (onAssessmentsLoaded && !loadingTrialAccess) {
      onAssessmentsLoaded(processedAssessments);
    }
  }, [processedAssessments, onAssessmentsLoaded, loadingTrialAccess]);

  // Check if assessment is enabled
  const isAssessmentEnabled = useCallback((assessment) => {
    if (loadingScreeners || screenerError || purchaseAccessError) {
      return false;
    }
    const key = normalizeId(assessment);
    const isConfigured = screenerSettings[key] === true;

    if (trialAccess?.restricted) {
      return isConfigured && (trialAccess.enabledAssessmentIds || []).includes(key);
    }

    return isScreenerAvailable(assessment, {
      isConfigured,
      purchaseAccessError,
      latestPurchase,
      isPilot,
    });
  }, [
    loadingScreeners,
    screenerError,
    purchaseAccessError,
    screenerSettings,
    trialAccess,
    latestPurchase,
    isPilot,
  ]);

  useEffect(() => {
    if (!selectAssessmentId || loadingScreeners || loadingTrialAccess) return;
    const selected = processedAssessments.find(
      (assessment) => normalizeId(assessment) === selectAssessmentId
    );
    if (!selected || !isAssessmentEnabled(selected)) {
      setSelectAssessmentId(null);
      setSelectAssessmentName(null);
    }
  }, [
    selectAssessmentId,
    processedAssessments,
    loadingScreeners,
    loadingTrialAccess,
    screenerSettings,
    setSelectAssessmentId,
    setSelectAssessmentName,
  ]);

  // Auto-select first enabled assessment
  useEffect(() => {
    if (!selectAssessmentId && processedAssessments.length > 0 && !loadingScreeners && !loadingAssessments && !loadingTrialAccess) {
      const enabledAssessments = processedAssessments.filter(isAssessmentEnabled);
      if (enabledAssessments.length > 0) {
        const firstEnabled = enabledAssessments[0];
        setSelectAssessmentId(normalizeId(firstEnabled));
        setSelectAssessmentName(firstEnabled.name);
      }
    }
  }, [
    selectAssessmentId,
    processedAssessments,
    setSelectAssessmentId,
    setSelectAssessmentName,
    loadingScreeners,
    loadingAssessments,
    loadingTrialAccess,
    isAssessmentEnabled,
  ]);

  // Render loading state
  if (loadingAssessments || loadingScreeners || loadingTrialAccess) {
    return (
      <Row className="margin-left">
        <Col>
          <div>Loading assessments and settings...</div>
        </Col>
      </Row>
    );
  }

  // Render error state
  if (assessmentError || screenerError || trialAccessError || purchaseAccessError) {
    return (
      <Row className="margin-left">
        <Col>
          <div>
            {purchaseAccessError
              ? "Error loading screener access. Please try again."
              : `Error loading ${trialAccessError ? "trial screener access" : assessmentError ? "assessments" : "settings"}. Please try again.`}
          </div>
        </Col>
      </Row>
    );
  }

  console.log("Final processed assessments:", processedAssessments);

  // Render empty state
  if (processedAssessments.length === 0) {
    return (
      <Row className="margin-left">
        <Col>
          <div>No assessments available for this country and grade level.</div>
        </Col>
      </Row>
    );
  }

  // Render assessments
  return (
    <Row className="margin-left">
      <Col>
        <div>
          {processedAssessments.map((assessment) => {
            const key = assessment.__idKey || normalizeId(assessment);
            const isEnabled = isAssessmentEnabled(assessment);
            const isSelected = selectAssessmentId === key;

            const handleClick = () => {
              if (isEnabled) {
                setSelectAssessmentId(key);
                setSelectAssessmentName(assessment.name);
              }
            };

            const getDisabledText = () => {
              if (trialAccess?.restricted) {
                if (
                  screenerSettings[key] === true &&
                  !(trialAccess.enabledAssessmentIds || []).includes(key)
                ) {
                  return "Not included in your trial";
                }
                return "Currently Disabled";
              }
              if (
                screenerSettings[key] === true &&
                !hasScreenerGradeAccess(assessment, {
                  latestPurchase,
                  isPilot,
                })
              ) {
                return isPurchaseExpired(latestPurchase)
                  ? "Subscription expired"
                  : "Not included in your subscription";
              }
              if (assessment.name?.toLowerCase().includes("baseline")) {
                // return "Screening open from 8th Sep to 17th Oct 2025";
              return "Currently Disabled";

              }
              return "Currently Disabled";
            };

            return (
              <button
                key={key}
                className={`light-blue-button assessment-button ${
                  isSelected ? "selected" : ""
                } ${!isEnabled ? "disabled" : ""}`}
                disabled={!isEnabled}
                aria-disabled={!isEnabled}
                onClick={handleClick}
              >
                {assessment.name}{" "}
                <img
                  src={`/assets/imgs/${assessment.imageName}-icon.png`}
                  className="season-assessment-icon"
                  alt={assessment.name}
                />
                {!isEnabled && (
                  <div className="small-text" style={{ fontSize: 16 }}>
                    {getDisabledText()}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </Col>
    </Row>
  );
}

export default AssessmentTable;


// import React, { useState, useEffect } from "react";
// import Row from "react-bootstrap/Row";
// import Col from "react-bootstrap/Col";
// import { getCountryScreeners, getSchoolScreeners } from "../services/APIClient"; // Update import path as needed
// import "./AssessmentTable.css";

// function AssessmentTable(props) {
//   const {
//     assessments,
//     selectAssessmentId,
//     setSelectAssessmentId,
//     setSelectAssessmentName,
//     classDetails,
//     gradeLevelOptions,
//     schoolid, // Add schoolId as a prop
//   } = props;

//   const [screenerSettings, setScreenerSettings] = useState({});
//   const [loadingScreeners, setLoadingScreeners] = useState(true);
//   const [screenerError, setScreenerError] = useState(null);

//   const gradeLevel1 = classDetails.grade_level1;
//   const gradeLevel2 = classDetails.grade_level2;

//   console.log(schoolid, "schoolId in AssessmentTable");

//   const gradeLevel1Ids = new Set([
//     "d613e45e-b3b0-4d0f-bcdc-cc17f38bd285",
//     "68ce3a92-73e0-4158-9c08-31bff6246d23",
//     "6a43ae18-2251-4876-8f15-23e2c897fdab"
//   ]);

//   const gradeLevel2Ids = new Set([
//     "927b2379-998c-4424-af56-1f82e0144765",
//     "9b962947-4f59-4e19-a725-40601b66e8ff",
//     "08e97976-f32f-4ad6-a2df-7a2be0789d70",
//   ]);

//   const assessmentOrder = {
//     baseline: 1,
//     midpoint: 2,
//     "end of year": 3,
//   };

//   // Fetch screener settings on component mount
//   useEffect(() => {
//     const fetchScreenerSettings = async () => {
//       if (!schoolid) {
//         console.error("❌ No schoolId provided to AssessmentTable");
//         setLoadingScreeners(false);
//         return;
//       }

//       try {
//         setLoadingScreeners(true);
//         setScreenerError(null);

//         console.log("🔍 Starting screener fetch for schoolId:", schoolid);

//         // First try to get school-specific screener settings
//         console.log("📞 Calling getSchoolScreeners with schoolId:", schoolid);
//         let screeners = await getSchoolScreeners(schoolid);
//         console.log("📥 getSchoolScreeners response:", screeners);
//         console.log("📊 getSchoolScreeners response type:", typeof screeners);
//         console.log("📊 getSchoolScreeners is array:", Array.isArray(screeners));
//         console.log("📊 getSchoolScreeners length:", screeners?.length);

//         // If no school-specific settings found, fallback to country settings
//         if (!screeners || screeners.length === 0) {
//           console.log("⚠️ No school screeners found, trying country screeners...");
//           console.log("📞 Calling getCountryScreeners with schoolId:", schoolid);
//           screeners = await getCountryScreeners(schoolid);
//           console.log("📥 getCountryScreeners response:", screeners);
//           console.log("📊 getCountryScreeners response type:", typeof screeners);
//           console.log("📊 getCountryScreeners is array:", Array.isArray(screeners));
//           console.log("📊 getCountryScreeners length:", screeners?.length);
//         } else {
//           console.log("✅ Using school-specific screeners");
//         }

//         // Convert array of screeners to a map for easy lookup
//         const screenerMap = {};
//         if (screeners && Array.isArray(screeners)) {
//           console.log("🔄 Converting screeners array to map...");
//           screeners.forEach((screener, index) => {
//             console.log(`📋 Screener ${index}:`, screener);
//             console.log(`  - assessmentid: ${screener.assessmentid}`);
//             console.log(`  - active: ${screener.active}`);
//             screenerMap[screener.assessmentid] = screener.active;
//           });
//         } else {
//           console.log("⚠️ Screeners is not a valid array:", screeners);
//         }

//         console.log("🗺️ Final screener map:", screenerMap);
//         setScreenerSettings(screenerMap);
//         console.log("✅ Screener settings updated successfully");

//       } catch (error) {
//         console.error("❌ Error fetching screener settings:", error);
//         console.error("❌ Error name:", error.name);
//         console.error("❌ Error message:", error.message);
//         console.error("❌ Error stack:", error.stack);
//         setScreenerError(error);
//         // Default to all screeners disabled on error
//         setScreenerSettings({});
//       } finally {
//         setLoadingScreeners(false);
//         console.log("🏁 Screener loading completed");
//       }
//     };

//     fetchScreenerSettings();
//   }, [schoolid]);

//   const sortAssessments = (a, b) => {
//     const getSortKey = (name) => {
//       name = name.toLowerCase();
//       if (name.includes("baseline")) return assessmentOrder["baseline"];
//       if (name.includes("midpoint")) return assessmentOrder["midpoint"];
//       if (name.includes("end of year")) return assessmentOrder["end of year"];
//       return 4;
//     };

//     let aOrder = getSortKey(a.name);
//     let bOrder = getSortKey(b.name);
//     return aOrder - bOrder;
//   };

//   const gradeAbbreviations = {
//     "Standard 1": "S1",
//     "Standard 2": "S2",
//     Reception: "Recep",
//     "Year 1": "Y1",
//     "Primary 1": "P1",
//     "Primary 2": "P2",
//     "Pre-K": "Pre-K",
//     "Kindergarten (K)": "K",
//     "KG1 (Kindergarten 1)": "KG1",
//     "KG2 (Kindergarten 2)": "KG2",
//     "Junior Infants": "Jnr",
//     "Senior Infants": "Snr",
//   };

//   let grade1Abbrev = gradeAbbreviations[gradeLevelOptions[0]?.grade1];
//   let grade2Abbrev = gradeAbbreviations[gradeLevelOptions[0]?.grade2];

//   const replacePrefix = (name) => {
//     if (gradeLevel1) {
//       return name.replace("Junior", grade1Abbrev || "Grade1");
//     } else if (gradeLevel2) {
//       return name.replace("Senior", grade2Abbrev || "Grade2");
//     }
//     return name;
//   };

//   // Helper function to check if an assessment is enabled based on screener settings
//   const isAssessmentEnabled = (assessmentId) => {
//     // If still loading or error occurred, default to disabled
//     if (loadingScreeners || screenerError) {
//       console.log(`🔒 Assessment ${assessmentId} disabled due to loading/error state`);
//       return false;
//     }

//     // Return the active status from screener settings, default to false if not found
//     const isEnabled = screenerSettings[assessmentId] === true;
//     console.log(`🔍 Assessment ${assessmentId} enabled status:`, isEnabled);
//     console.log(`🔍 Screener setting value:`, screenerSettings[assessmentId]);
//     return isEnabled;
//   };

//   let assessmentsArray = [];

//   if (gradeLevel1) {
//     assessments.forEach((assessment) => {
//       if (gradeLevel1Ids.has(assessment.id)) {
//         let imageName;

//         if (assessment.name.toLowerCase().includes("baseline")) {
//           imageName = "autumn";
//         } else if (assessment.name.toLowerCase().includes("midpoint")) {
//           imageName = "spring";
//         } else if (assessment.name.toLowerCase().includes("end of year")) {
//           imageName = "summer";
//         } else {
//           imageName = assessment.name.split(" ")[0];
//         }

//         assessmentsArray.push({
//           ...assessment,
//           name: replacePrefix(assessment.name, "Junior"),
//           imageName: imageName.toLowerCase(),
//         });
//       }
//     });
//     assessmentsArray.sort(sortAssessments);
//   }

//   if (gradeLevel2) {
//     assessments.forEach((assessment) => {
//       if (gradeLevel2Ids.has(assessment.id)) {
//         let imageName;

//         if (assessment.name.toLowerCase().includes("baseline")) {
//           imageName = "autumn";
//         } else if (assessment.name.toLowerCase().includes("midpoint")) {
//           imageName = "spring";
//         } else if (assessment.name.toLowerCase().includes("end of year")) {
//           imageName = "summer";
//         } else {
//           imageName = assessment.name.split(" ")[0];
//         }

//         assessmentsArray.push({
//           ...assessment,
//           name: replacePrefix(assessment.name, "Senior"),
//           imageName: imageName.toLowerCase(),
//         });
//       }
//     });
//     assessmentsArray.sort(sortAssessments);
//   }

//   console.log("📋 Final assessments array:", assessmentsArray);
//   console.log("📋 Assessments count:", assessmentsArray.length);

//   // Auto-select the first enabled assessment when component loads
//   useEffect(() => {
//     if (!selectAssessmentId && assessmentsArray.length > 0 && !loadingScreeners) {
//       console.log("🎯 Auto-selecting first enabled assessment...");
//       // Find the first enabled assessment in order of priority
//       const enabledAssessments = assessmentsArray.filter(assessment =>
//         isAssessmentEnabled(assessment.id)
//       );

//       console.log("✅ Enabled assessments:", enabledAssessments);
//       console.log("✅ Enabled assessments count:", enabledAssessments.length);

//       if (enabledAssessments.length > 0) {
//         const firstEnabled = enabledAssessments[0];
//         console.log("🎯 Auto-selecting assessment:", firstEnabled);
//         setSelectAssessmentId(firstEnabled.id);
//         setSelectAssessmentName(firstEnabled.name);
//       } else {
//         console.log("⚠️ No enabled assessments found for auto-selection");
//       }
//     }
//   }, [
//     selectAssessmentId,
//     assessmentsArray,
//     setSelectAssessmentId,
//     setSelectAssessmentName,
//     loadingScreeners,
//     screenerSettings,
//   ]);

//   // Show loading state
//   if (loadingScreeners) {
//     return (
//       <Row className="margin-left">
//         <Col>
//           <div>Loading assessment settings...</div>
//         </Col>
//       </Row>
//     );
//   }

//   // Show error state
//   if (screenerError) {
//     return (
//       <Row className="margin-left">
//         <Col>
//           <div>Error loading assessment settings. Please try again.</div>
//         </Col>
//       </Row>
//     );
//   }

//   return (
//     <Row className="margin-left">
//       <Col>
//         <div>
//           {assessmentsArray.map((assessment, index) => {
//             const isEnabled = isAssessmentEnabled(assessment.id);
//             console.log(`🔘 Rendering assessment ${assessment.name} (${assessment.id}): enabled=${isEnabled}`);

//             return (
//               <button
//                 key={index}
//                 className={`light-blue-button assessment-button ${selectAssessmentId === assessment.id ? "selected" : ""
//                   } ${!isEnabled ? "disabled" : ""}`}
//                 onClick={() => {
//                   if (isEnabled) {
//                     console.log("👆 Assessment clicked:", assessment.name, assessment.id);
//                     setSelectAssessmentId(assessment.id);
//                     setSelectAssessmentName(assessment.name);
//                   } else {
//                     console.log("🚫 Disabled assessment clicked:", assessment.name);
//                   }
//                 }}
//               >
//                 {assessment.name}{" "}
//                 <img
//                   src={`/assets/imgs/${assessment.imageName}-icon.png`}
//                   className="season-assessment-icon"
//                   alt={assessment.name}
//                 />
//                 {!isEnabled && (
//                   <div className="small-text" style={{ fontSize: 16 }}>
//                     {assessment.name?.toLowerCase().includes("baseline")
//                       ? "Screening open from 8th Sep to 17th Oct 2025"
//                       : "Currently Disabled"}
//                   </div>
//                 )}
//               </button>
//             );
//           })}
//         </div>
//       </Col>
//     </Row>
//   );
// }

// export default AssessmentTable;
